import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@htx/environment';
import { AuditLogEntry, AuditLogFilter } from './audit-log.model';
import { SpringPageApiDto, PaginatedList, PageQueryParams } from '../organizations/organization.model';
import { HttpUtilsService } from '../http-utils.service';

@Injectable({
  providedIn: 'root'
})
export class AuditLogService {

  private httpClient = inject(HttpClient);
  private httpUtils = inject(HttpUtilsService);
  private readonly baseUrl = `${environment.backendUrl}/api/audit-logs`;

  findAll(
    filter: AuditLogFilter = {},
    queryParams: PageQueryParams = { page: 0, size: 20, sort: 'occurredAt,desc' }
  ): Observable<PaginatedList<AuditLogEntry>> {
    let params = this.httpUtils.toHttpParams(queryParams);
    if (filter.from) params = params.set('from', filter.from);
    if (filter.to) params = params.set('to', filter.to);
    if (filter.action) params = params.set('action', filter.action);
    if (filter.resourceType) params = params.set('resourceType', filter.resourceType);
    if (filter.actorIamId) params = params.set('actorIamId', filter.actorIamId);
    if (filter.organizationId) params = params.set('organizationId', filter.organizationId);
    if (filter.outcome) params = params.set('outcome', filter.outcome);
    if (filter.q) params = params.set('q', filter.q);

    return this.httpClient
      .get<SpringPageApiDto<AuditLogEntry>>(this.baseUrl, { params })
      .pipe(
        map((response) => ({
          content: response.content,
          pagination: {
            pageNumber: response.pageable.pageNumber,
            pageSize: response.pageable.pageSize,
            totalElements: response.totalElements,
            totalPages: response.totalPages,
            last: response.last,
            first: response.first,
            numberOfElements: response.numberOfElements,
            size: response.size,
            number: response.number,
            empty: response.empty
          },
          sort: response.sort
        }))
      );
  }

  getById(id: string): Observable<AuditLogEntry> {
    return this.httpClient.get<AuditLogEntry>(`${this.baseUrl}/${id}`);
  }

  exportCsv(filter: AuditLogFilter = {}): void {
    this.findAll(filter, { page: 0, size: 10000, sort: 'occurredAt,desc' }).subscribe((result) => {
      const headers = ['Datum', 'Akteur', 'Akteur-ID', 'Rolle', 'Aktion', 'Ressource-Typ', 'Ressource-ID', 'Ergebnis', 'HTTP-Methode', 'HTTP-Pfad', 'IP-Adresse'];
      const rows = result.content.map((e) =>
        [
          e.occurredAt,
          e.actorName ?? '',
          e.actorIamId ?? '',
          e.actorRole ?? '',
          e.action,
          e.resourceType ?? '',
          e.resourceId ?? '',
          e.outcome,
          e.httpMethod ?? '',
          e.httpPath ?? '',
          e.clientIp ?? ''
        ].map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      );
      const csv = [headers.join(','), ...rows].join('\r\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }
}
